import { useNavigate, useParams } from "react-router-dom";

// assets

import {
  Typography,
  Stack,
  Button,
  Box,
  Divider,
  AppBar,
  Toolbar,
  ThemeProvider,
  Drawer,
  IconButton,
  Fab,
} from "@mui/material";
import {
  ContentWrapNarrow,
  CreatedWith,
  CardStyle,
  InfoBox,
} from "Components/AllComponents";
import { createCustomTheme } from "Components/Styles/Styles";
import { PresaleItems } from "Pages/CustomerView/CustomerPresale/Components/PresaleItems";
import { useSession } from "Context/userAuthContext";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { selectPresaleById } from "Redux/selectors";
import _ from "lodash";
import {
  defaultPresaleInfo,
  formatFulfillments,
  getPresaleName,
} from "Model/PresaleInfo";
import { PresaleForm } from "Pages/CustomerView/CustomerPresale/Components/PresaleForm";
import { useEffect, useState } from "react";
import { initializeCart } from "Redux/actionCreators";

import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

const drawerWidth = 390;

const PresalePreview = () => {
  const { presaleId } = useParams<"presaleId">();
  const { sellerProfile } = useSession();
  const presale =
    useAppSelector((state) => selectPresaleById(state, presaleId || "")) ||
    defaultPresaleInfo(
      sellerProfile.defaultPickupAddress ? true : false,
      sellerProfile.payments.map((p) => p.method)
    );
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Need to initialize cart, using redux
  useEffect(() => {
    dispatch(initializeCart(presale.presaleItems));
  }, [presale]);

  return (
    <ThemeProvider theme={createCustomTheme(sellerProfile.colorPalette)}>
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mr: { md: `${drawerWidth}px` },
        }}
      >
        <Stack
          spacing={{ xs: 2, sm: 3 }}
          sx={{
            p: { xs: 2, sm: 3 },
            pb: { xs: 10, md: 3 },
            backgroundColor: "background.default",
            minHeight: "100vh",
          }}
        >
          <AppBar
            sx={{
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backgroundColor: "background.default",
            }}
          >
            <Toolbar variant="dense">
              <Typography
                variant="h6"
                color="text.primary"
                component="div"
                flex={1}
              >
                Presale preview
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={() => {
                    navigate(`/admin/presales/${presale.presaleId}#items`);
                  }}
                >
                  Edit presale
                </Button>
              </Stack>
            </Toolbar>
          </AppBar>

          {/* Mobile Cart Button */}
          <Box
            sx={{
              display: { xs: "block", md: "none" },
              position: "fixed",
              bottom: 16,
              right: 16,
              zIndex: 1100,
            }}
          >
            <Fab
              variant="extended"
              color="primary"
              onClick={() => setMobileDrawerOpen(true)}
            >
              Continue
            </Fab>
          </Box>

          <Box sx={{ pt: 4 }}>
            <ContentWrapNarrow>
              <CardStyle>
                <Stack>
                  <Typography variant="h5" sx={{ mb: 1, textAlign: "center" }}>
                    {getPresaleName(presale.presaleName)}
                  </Typography>
                  {presale.presaleDescription ? (
                    <Typography
                      variant="body1"
                      color="text.primary"
                      textAlign="center"
                      sx={{ mb: 2, whiteSpace: "pre-line" }}
                    >
                      {presale.presaleDescription}
                    </Typography>
                  ) : null}
                  <InfoBox>
                    <Typography variant="subtitle2" textAlign="center">
                      Pickup on {formatFulfillments(presale.fulfillments)}
                    </Typography>
                  </InfoBox>
                </Stack>
              </CardStyle>
            </ContentWrapNarrow>
          </Box>

          <PresaleItems presaleItems={presale.presaleItems} />
        </Stack>
      </Box>

      {/* Persistent Drawer */}
      <Drawer
        variant="permanent"
        anchor="right"
        sx={{
          display: { xs: "none", md: "block" },
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            top: 49,
            height: "calc(100% - 49px)",
          },
        }}
      >
        <Box sx={{ overflowY: "auto", height: "100%" }}>
          <PresaleForm
            presaleInfo={presale}
            editing={true}
            sellerProfile={sellerProfile}
          />
        </Box>
      </Drawer>

      {/* Mobile: Temporary Drawer */}
      <Drawer
        variant="temporary"
        anchor="bottom"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        keepMounted={true}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            maxHeight: "90vh",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        }}
      >
        <Box sx={{ pb: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 2, pb: 0 }}
          >
            <Typography variant="h6">Your Order</Typography>
            <IconButton onClick={() => setMobileDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <PresaleForm
            presaleInfo={presale}
            editing={true}
            sellerProfile={sellerProfile}
          />
        </Box>
      </Drawer>
    </ThemeProvider>
  );
};

export default PresalePreview;
